// Google Sign-In button — dual path.
//
// WEB (PWA at kinly.geminii-ai.com): Google Identity Services (GSI) JS SDK
//   via google.accounts.id.initialize + prompt(). Returns an id_token.
// NATIVE iOS (TestFlight / App Store): @capgo/capacitor-social-login plugin
//   uses the native GIDSignIn iOS SDK. Returns an id_token whose `aud` is
//   the iOS OAuth client_id (different from the web one).
//
// Both paths POST { id_token } to /api/auth/social/google/native/callback.
// Backend accepts BOTH audiences (web + iOS client_id) — see social_auth.py.
import React from "react";
import { api, formatApiError } from "@/lib/api";
import {
  isNativeIos,
  ensureSocialLoginInitialized,
  getSocialLogin,
  withLoginTimeout,
} from "@/lib/socialLogin";

const GSI_SDK_URL = "https://accounts.google.com/gsi/client";
const GOOGLE_CLIENT_ID =
  "426366993010-gpk117q6t35o12udi0b3n4pg7utbnluo.apps.googleusercontent.com";

function ensureGsiLoaded() {
  if (typeof window === "undefined") return Promise.reject(new Error("SSR"));
  if (window.google?.accounts?.id) return Promise.resolve();
  const existing = document.getElementById("kinly-gsi-sdk");
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Google SDK failed to load")));
    });
  }
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.id = "kinly-gsi-sdk";
    s.src = GSI_SDK_URL;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Google SDK failed to load"));
    document.head.appendChild(s);
  });
}

const GOOGLE_G = (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
    />
    <path
      fill="#FBBC05"
      d="M3.964 10.707A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.039l3.007-2.332z"
    />
    <path
      fill="#EA4335"
      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"
    />
  </svg>
);

async function postIdTokenToBackend(idToken) {
  const { data } = await api.post("/auth/social/google/native/callback", {
    id_token: idToken,
  });
  localStorage.setItem(
    "kinly.tokens",
    JSON.stringify({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
    })
  );
  window.location.href = "/today";
}

export function GoogleAuthButton({ disabled = false, label = "Continue with Google" }) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const native = isNativeIos();

  // Web-only: pre-load + init GSI on mount. Native skips this entirely.
  React.useEffect(() => {
    if (native) return;
    let cancelled = false;
    (async () => {
      try {
        await ensureGsiLoaded();
        if (cancelled) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
          ux_mode: "popup",
          auto_select: false,
          itp_support: true,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("GSI init failed", e);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [native]);

  const handleCredentialResponse = async (response) => {
    const idToken = response?.credential;
    if (!idToken) {
      setBusy(false);
      setError("Google did not return a credential");
      return;
    }
    try {
      await postIdTokenToBackend(idToken);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(formatApiError(detail) || e?.message || "Google sign-in failed");
      setBusy(false);
    }
  };

  const onClickNative = async () => {
    try {
      await ensureSocialLoginInitialized();
      const SocialLogin = getSocialLogin();
      if (!SocialLogin) throw new Error("Native social login unavailable");
      if (!process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID) {
        throw new Error("Google iOS client id missing — rebuild with REACT_APP_GOOGLE_IOS_CLIENT_ID");
      }
      const result = await withLoginTimeout(SocialLogin.login({
        provider: "google",
        options: { scopes: ["email", "profile"] },
      }));
      const r = result?.result || result || {};
      const idToken =
        r.idToken ||
        r.accessToken?.idToken ||
        r.identityToken ||
        r.authentication?.idToken ||
        null;
      if (!idToken) {
        console.error("Google native login returned no id_token", result);
        throw new Error("Google did not return an id_token");
      }
      await postIdTokenToBackend(idToken);
    } catch (e) {
      const code = e?.error || e?.code || "";
      if (code === "user_cancelled" || code === "12501" || code === "USER_CANCELLED") {
        setBusy(false);
        return;
      }
      const detail = e?.response?.data?.detail;
      const msg = formatApiError(detail) || e?.message || JSON.stringify(e) || "Google sign-in failed";
      console.error("[Google native]", e);
      setError(msg);
      setBusy(false);
    }
  };

  const onClickWeb = async () => {
    try {
      await ensureGsiLoaded();
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed?.() || notification.isSkippedMoment?.()) {
          setBusy(false);
          setError("Google sign-in unavailable — try again in a few seconds, or use email & password.");
        }
      });
    } catch (e) {
      setError(e?.message || "Google sign-in failed to start");
      setBusy(false);
    }
  };

  const onClick = async () => {
    if (busy) return;
    setError("");
    setBusy(true);
    if (native) await onClickNative();
    else await onClickWeb();
  };

  return (
    <>
      <button
        type="button"
        data-testid="signin-google"
        onClick={onClick}
        disabled={disabled || busy}
        className="w-full h-14 rounded-2xl border-2 border-border bg-background text-foreground font-medium tap flex items-center justify-center gap-3 disabled:opacity-60 hover:border-foreground/40 transition-colors"
      >
        {GOOGLE_G}
        <span className="text-[15px]">{busy ? "Signing in…" : label}</span>
      </button>
      {error && (
        <p
          data-testid="signin-google-error"
          className="mt-2 text-xs text-destructive bg-destructive/10 rounded-lg px-2 py-1"
        >
          {error}
        </p>
      )}
    </>
  );
}
